import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  FormControlLabel,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  CircularProgress,
} from "@mui/material";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useLocation } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import { useState, useEffect, useRef } from "react";
import moment from "moment-timezone";
import { useFormik } from "formik";
import { loadStripe, Stripe, StripeCardElement, StripeElements } from "@stripe/stripe-js";
import { colors } from "@/theme";

import { formatAxiosError, formatCurrency, parsePaymentSystemError } from "@/utils";
import { useCart, useDealer } from "@/contexts";
import { roundCurrency } from "@/utils";
import { CartHelper } from "@/helpers";
import { useDataApi } from "@/hooks";

import "./style.css";
import { CustomCheckbox, CustomTextField } from "./customStyles";
import { states } from "./constants";
import { hasOffsiteProducts } from "@/helpers/cart";
import PaymentFailurePopup from "./PaymentFailurePopup";

// Lazily initialize Stripe once at module scope. Safe to call outside components.
const stripePromise: Promise<Stripe | null> = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
);

export interface FedexApiResponse {
  streetLines: string[]
  city: string
  stateOrProvinceCode: string
  countryCode: string
  zipCodeBase: any
  zipCodeAddOn: any
}

const Checkout: React.FC = () => {
  
  console.time("StartTime")
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const cardElementRef = useRef<StripeCardElement | null>(null);
  const cardMountRef = useRef<HTMLDivElement | null>(null);
  const [stripeReady, setStripeReady] = useState(false);
  const { cart, setDiscount, setDiscountTypeWithCartType, setPaymentMethod, setOrderId, setDiscountComment } = useCart();
  const location = useLocation();
  const mode = location.state?.mode; // 'offsite' or 'in-store'
  // Filter products based on mode
  const filteredProducts = cart?.products?.filter((p) => (mode === "offsite" ? p.offsite === true : p.offsite === false)) || [];
  const filteredCart = cart ? { ...cart, products: filteredProducts } : undefined;
  const hasShippingItems = filteredCart?.products?.some((product) => product.selectedToShip || (product.shippingQuantity ?? 0) > 0);
  const isInStoreNoShipping = mode === "in-store" && !hasShippingItems;
  // Calculate initial boxes based on shipping items
  const initialBoxes = filteredCart?.products?.reduce((sum, product) => {
    if (product.offsite === true) {
      return sum + (product.quantity || 0);
    }
    if (product.selectedToShip === true || (product.shippingQuantity ?? 0) > 0) {
      return sum + (product.shippingQuantity || 0);
    }
    return sum;
  }, 0) || 0;
  const [addressType, setAddressType] = useState("residential");
  const boxesInputRef = useRef<HTMLInputElement>(null);
  const companyInputRef = useRef<HTMLInputElement>(null);
  const contactNameInputRef = useRef<HTMLInputElement>(null);
  const address1InputRef = useRef<HTMLInputElement>(null);
  const address2InputRef = useRef<HTMLInputElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const stateInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const { dealer, coopInfo } = useDealer();
  const [isPostCompleted, setIsPostCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<any>(undefined);
  const { error, post, response, isLoading, } = useDataApi();
  const [isAddressUpdateLoading, setIsAddressUpdateLoading] = useState(false)
  // console.log("dealer ", dealer, " coopInfo:", coopInfo);

  // State declarations
  const [isEditingBoxes, setIsEditingBoxes] = useState(!isInStoreNoShipping);
  const [boxesCount, setBoxesCount] = useState(mode === "in-store" ? "0" : (initialBoxes ? String(initialBoxes) : ""));
  const [isFormValid, setIsFormValid] = useState(false);
  const [isBoxesUpdated, setIsBoxesUpdated] = useState(isInStoreNoShipping);
  const [isShippingUpdated, setIsShippingUpdated] = useState(!hasShippingItems);
  const [isCreditCardUpdated, setIsCreditCardUpdated] = useState(false);
  const [boxesError, setBoxesError] = useState("");
  const navigate = useNavigate();

  const testRef = useRef("")

  // Shipping details state
  const [isEditingShipping, setIsEditingShipping] = useState(true);
  const [company, setCompany] = useState(dealer?.company);
  const [contactName, setContactName] = useState(dealer?.firstName + " " + dealer?.lastName);
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState(dealer?.phone);
  const [email, setEmail] = useState(dealer?.email);
  const [deliveryInstructions, setDeliveryInstructions] = useState("");

  const [isEditingCreditCard, setIsEditingCreditCard] = useState(true);

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<"suggested" | "original">("suggested");

  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountValue, setDiscountValue] = useState(mode == "offsite" ? (filteredCart?.offSiteDiscount ?? 0).toFixed(2) : (filteredCart?.discount ?? 0).toFixed(2));
  const [discount, setDiscountWithType] = useState({
    type: dealer?.percentageOff ? 'percentage' : 'amount' as 'percentage' | 'amount',
    value: dealer?.percentageOff ? dealer.percentageOff * 100 : 0
  });
  const [discountNote, setDiscountNote] = useState("");

  const [errorPopupOpen, setErrorPopupOpen] = useState(false);
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingAmount, setShippingAmount] = useState("");
  const [customShippingFee, setCustomShippingFee] = useState<number | undefined>(undefined);
  const [suggestedAddress, setSuggestedAddress] = useState<{ name: string, address1: string, cityStateZip: string } | undefined>(undefined);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [giftCardInput, setGiftCardInput] = useState("");
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [isGiftCardApplied, setIsGiftCardApplied] = useState(false);
  const nameOnCardRef = useRef<HTMLInputElement>(null);
  const updateCardRef = useRef<HTMLButtonElement>(null);
  const placeOrderButtonRef = useRef<HTMLButtonElement>(null);
  const [stripeError, setStripeError] = useState<string | undefined>(undefined);
  const [isStripeProcessing, setIsStripeProcessing] = useState(false);


  // Helper to check if address verification is needed
  const needsAddressVerification = filteredCart?.products?.some(
    (product) => product.selectedToShip || product.offsite || (product.shippingQuantity ?? 0) > 0
  );

  // Check if we're in in-store mode with no shipping items
  const originalAddress = {
    name: `${dealer?.firstName} ${dealer?.lastName}`,
    address1: address1,
    cityStateZip: `${city}, ${state} ${zip}`,
  };

  const formik = useFormik({
    initialValues: {
      nameOnCard: "",
      email: email || "",
    },
    onSubmit: (values) => {
      console.log(values);
      // Handle submission
    },
  });

  const handleAddressTypeChange = (type: string) => {
    setAddressType(type);
  };

  const handleBoxesUpdate = () => {
    const numBoxes = parseInt(boxesCount);
    if (isNaN(numBoxes)) {
      setBoxesError("Please enter a valid number");
      return;
    }
    setBoxesError("");
    setIsEditingBoxes(false);
    setIsBoxesUpdated(true);
    // Always reset to default cart shipping fee when boxes are modified
    setCustomShippingFee(undefined);
  };

  const handleBoxesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBoxesCount(value);
    if (value === "") {
      setBoxesError("");
    } else if (isNaN(parseInt(value))) {
      setBoxesError("Please enter a valid number");
    } else {
      setBoxesError("");
    }
  };

  const handleBoxesEdit = () => {
    setIsEditingBoxes(true);
    setIsBoxesUpdated(false);
  };

  const handleShippingUpdate = async () => {
    setIsAddressUpdateLoading(true)
    try {
      // Make FedEx address validation API call
      const addressValidationData = {
        address: address1,
        city: city,
        state: state,
        zip: zip
      };

      const response = await fetch(`${process.env.REACT_APP_REST_BASE_URL}/FedEx/validateaddress?address=${address1}&city=${city}&state=${state}&zip=${zip}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data: FedexApiResponse[] = await response.json();
      console.log('FedEx Address Validation Response:', data);
      setSuggestedAddress({
        name: `${dealer?.firstName} ${dealer?.lastName}`,
        address1: data[0].streetLines[0],
        cityStateZip: `${data[0].city}, ${data[0].stateOrProvinceCode ?? state}, ${data[0].zipCodeBase ?? zip}`,
      });


      console.log('needsAddressVerification', needsAddressVerification);
      // Show the FedEx address verification dialog whenever the dealer has
      // entered any part of an address. The previous condition only fired
      // when at least one cart product had a shipping-related flag set,
      // which silently accepted whatever was typed for in-store orders or
      // when product flags hadn't been set. Now: if you typed an address,
      // you get a confirmation popup. If you typed nothing, no popup.
      const hasEnteredAddress = !!(address1 || city || state || zip);
      if (hasEnteredAddress) {
        setShowAddressDialog(true);
      } else {
        setIsEditingShipping(false);
        setIsShippingUpdated(true);
        // Add focus to name on card field after shipping update
        cardElementRef.current?.focus();
      }
    } catch (error) {
      console.error('Error validating address:', error);
      // Continue with existing shipping update logic even if validation fails
      setIsEditingShipping(false);
      setIsShippingUpdated(true);
      cardElementRef.current?.focus();
    }
    finally {
      setIsAddressUpdateLoading(false)
    }
  };

  const handleShippingEdit = () => {
    setIsEditingShipping(true);
    setIsShippingUpdated(false);
  };

  // Form validation function
  const validateForm = () => {
    const hasShippingItems = filteredCart?.products?.some(
      (product) => product.selectedToShip || (product.shippingQuantity ?? 0) > 0
    );
    const isGiftCardGreaterThanTotal = giftCardAmount >= getTotalAmount();

    // console.log('Validation Debug:', {
    //   hasShippingItems, isOffsite: hasOffsiteProducts(filteredCart)
    // });

    const requiredFields = (() => {
      // Base shipping fields
      const shippingFields = [
        company,
        contactName,
        address1,
        city,
        state,
        zip,
        phone,
        email,
      ];

      // Check if credit card is needed
      const isCreditCardNeeded = !(isGiftCardApplied && giftCardAmount >= getTotalAmount());

      // For Stripe, credit card validation is handled by Stripe Elements + isCreditCardUpdated flag.
      // Name on card is intentionally NOT required here — Stripe doesn't require billing_details.name
      // for the payment to succeed, and forcing dealers to type it after every swipe slows event
      // checkouts down. Field is still rendered so dealers CAN type it if they want; it's just optional.
      const creditCardFields: string[] = [];

      // Case 1: In-store order without shipping items
      if (mode === "in-store" && !hasShippingItems) {
        return [
          boxesCount,
          ...creditCardFields,
        ];
      }

      // Case 2: In-store order with shipping items
      if (mode === "in-store" && hasShippingItems) {
        return [
          boxesCount,
          ...shippingFields,
          ...creditCardFields,
        ];
      }

      // Case 3: Offsite order
      if (mode === "offsite") {
        return [
          boxesCount,
          ...shippingFields,
          ...creditCardFields,
        ];
      }

      // Default case (should not happen)
      return [];
    })();

    // console.log('Required Fields:', requiredFields);

    // Check if all required fields are filled
    const isValid = requiredFields.filter(item => item).every((field) => field && field.trim() !== "");
    setIsFormValid(isValid);
  };

  // Helper function to get current shipping fee
  const getCurrentShippingFee = () => {
    // If custom shipping fee is set, use it directly without multiplying
    if (customShippingFee !== undefined) {
      return customShippingFee;
    }
    // Otherwise use default cart shipping fee and multiply by boxes if boxes > 0
    const baseFee = CartHelper.getShippingFee(filteredCart) || 0;
    const boxes = parseInt(boxesCount) || 0;
    return boxes > -1 ? baseFee * boxes : baseFee;
  };

  const getTotalAmount = () => (
    Math.max(CartHelper.getCartSubtotal(filteredCart) -
      (parseFloat(discountValue) || 0) -
      CartHelper.getCartItemDiscountTotal(filteredCart) -
      CartHelper.getSubtotalCoopTotal(filteredCart) -
      (isGiftCardApplied ? giftCardAmount : 0) +
      (getCurrentShippingFee() || 0), 0)
  )
  // Refactored handlePlaceOrder
  const handlePlaceOrder = async () => {
    if (isFormValid) {
      // === Begin moved API call logic ===
      // const currentDateTime = moment().tz("America/Los_Angeles").format();
      const orderId = filteredCart?.orderId;
      const orderIdentifier = filteredCart?.orderIdentifier;
      // const estimatedDeliveryDate = moment().tz("America/Los_Angeles").add(7, "days").format();
      // derive shipping contact first/last name from edited contactName (fallback to dealer)
      const _contactNameParts = contactName ? contactName.trim().split(/\s+/) : [];
      const _shippingFirstName = _contactNameParts.length > 0 ? _contactNameParts[0] : (dealer?.firstName || "");
      const _shippingLastName = _contactNameParts.length > 1 ? _contactNameParts.slice(1).join(" ") : (dealer?.lastName || "");

      const orderData = {
        products: filteredCart?.products?.map((product) => {
          const selectedToShip = Boolean(product.selectedToShip || (product.shippingQuantity ?? 0) > 0);
          return {
            ...product,
            selectedToShip,
          };
        }),
        discount:
          (mode == "offsite"
            ? (filteredCart?.offSiteDiscount || 0)
            : (filteredCart?.discount || 0)) +
          CartHelper.getCartItemDiscountTotal(filteredCart),
        discountComment: (() => {
          const orderComment = cart?.discountComment || "";
          const itemComments = (filteredCart?.products || [])
            .filter((p) => (p.itemDiscount || 0) > 0)
            .map((p) => {
              const note = p.itemDiscountComment
                ? ` — ${p.itemDiscountComment}`
                : "";
              return `${p.productDescription || p.sku}: -${(p.itemDiscount || 0).toFixed(2)}${note}`;
            })
            .join("\n");
          return [orderComment, itemComments].filter(Boolean).join("\n");
        })(),
        shippingFee: getCurrentShippingFee(),
        shippingData: {
          shippingContact: {
            firstName: _shippingFirstName,
            lastName: _shippingLastName,
            email: email,
            phone: phone,
          },
          shippingAddress: (() => {
            const useSuggested = selectedAddress == "suggested" && suggestedAddress !== undefined;
            return {
              street: useSuggested ? suggestedAddress!.address1 : address1,
              city: useSuggested ? suggestedAddress!.cityStateZip.split(",")[0] : city,
              state: useSuggested ? suggestedAddress!.cityStateZip.split(",")[1].trim() : state,
              zip: useSuggested ? suggestedAddress!.cityStateZip.split(",")[2].trim() : zip,
            };
          })(),
        },
        paymentMethodId: filteredCart?.paymentMethodId,
        orderId: orderId,
        orderIdentifier: filteredCart?.orderIdentifier || orderIdentifier,
        dealerId: dealer?.dealerId,
        masterDealerId: coopInfo?.masterDealerId,
        total: roundCurrency(
          CartHelper.getCartSubtotal(filteredCart)),
        coopUsed: roundCurrency(CartHelper.getSubtotalCoopTotal(filteredCart)),
        creditCardUsed: roundCurrency(getTotalAmount() || 0),
        ship: hasShippingItems,
        trackingNumber: "",
        claimFee: filteredCart?.claimFee || 0,
        couponAmount: filteredCart?.couponAmount,
        couponQuantity: filteredCart?.couponQuantity || 0,
        couponUsed: roundCurrency(giftCardAmount || 0),
        dealerFirstName: dealer?.firstName || "",
        dealerLastName: dealer?.lastName || "",
        email: email,
        // dateTimeCreated: currentDateTime,
        // estimatedDeliveryDate: estimatedDeliveryDate,
        errorOccured: filteredCart?.errorOccured,
        errorDescription: filteredCart?.errorDescription,
        numberOfBoxes: parseInt(boxesCount) || 0,
        status: 0,
        offsite: mode === "offsite",
      };
      console.log("Order ID:", filteredCart?.orderId);
      console.log("Sending order data:", orderData);
      setIsPostCompleted(false);
      setErrorMessage(undefined);
      await post(`${process.env.REACT_APP_REST_BASE_URL}/orders/pay`, orderData);
      setIsPostCompleted(true);
      // === End moved API call logic ===
    }
  };

  // Handler for dialog confirm
  const handleUseSelectedAddress = async () => {
    console.log('Address Selection:', {
      selectedAddress,
      suggestedAddress,
      originalAddress
    });
    setShowAddressDialog(false);
    setIsEditingShipping(false);
    setIsAddressUpdateLoading(false);
    setIsShippingUpdated(true);

    setTimeout(() => {
      cardElementRef.current?.focus();
      validateForm();
    }, 100);
  };

  const handleCreditCardUpdate = async () => {
    const stripe = stripeRef.current;
    const cardElement = cardElementRef.current;
    if (!stripe || !cardElement) {
      return;
    }

    setIsStripeProcessing(true);
    setStripeError(undefined);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      billing_details: {
        name: formik.values.nameOnCard,
      },
    });

    setIsStripeProcessing(false);

    if (error) {
      setStripeError(error.message);
      return;
    }

    if (paymentMethod) {
      setPaymentMethod?.(
        paymentMethod.id,
        paymentMethod.card?.brand,
        paymentMethod.card?.last4,
      );
      setIsCreditCardUpdated(true);
    }
  };

  const handleGiftCardUpdate = () => {
    if (giftCardInput && !isGiftCardApplied) {
      console.log('Gift Card Update:', {
        amount: Number(giftCardInput),
        total: getTotalAmount()
      });
      setGiftCardAmount(Number(giftCardInput));
      setIsGiftCardApplied(true);
      setTimeout(() => {
        validateForm();
      }, 0);
    }
  }

  const handleBoxesKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && boxesCount && !boxesError) {
      handleBoxesUpdate();
      if (companyInputRef.current) {
        companyInputRef.current.focus();
      }
    }
  };

  const handleShippingFieldKeyPress = (e: React.KeyboardEvent, nextRef: React.RefObject<HTMLInputElement | null>) => {
    if (e.key === 'Enter' && (e.target as HTMLInputElement).value) {
      if (nextRef.current) {
        nextRef.current.focus();
      }
    }
  };

  const handleAddressTypeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const newType = addressType === "residential" ? "commercial" : "residential";
      setAddressType(newType);
      if (companyInputRef.current) {
        companyInputRef.current.focus();
      }
    }
  };

  const handleDiscountUpdate = (defaultPercentage?: number) => {
    let discountAmount = 0
    const total = Number((Math.max(CartHelper.getCartSubtotal(filteredCart) -
      (parseFloat(discountValue) || 0) -
      CartHelper.getSubtotalCoopTotal(filteredCart) +
      (getCurrentShippingFee() || 0), 0) || 0)) + (parseFloat(discountValue) || 0)
    if (discount.type == "percentage") {
      const discountPercentage = defaultPercentage || discount.value || 0;
      discountAmount = total * discountPercentage / 100
    }
    else {
      discountAmount = discount.value || 0;
    }
    if (discountAmount > total) {
      alert("Discount cannot be greater than the subtotal amount");
      return;
    }
    setDiscountValue(`${discountAmount}`)
    if (setDiscount) {
      setDiscountTypeWithCartType?.({ storeType: "in-store", value: discount.value ?? 0, type: discount.type })
      setDiscountComment?.(discountNote)
      setDiscount(discountAmount, mode == "offsite");
      if (filteredCart) {
        if (mode === "offsite") filteredCart.offSiteDiscount = discountAmount;
        else filteredCart.discount = discountAmount;
      }
    }
    setDiscountDialogOpen(false);
  }

  const isDisabled = () => {
    // For in-store orders without shipping items, ignore shipping section state
    const shouldCheckShipping = (mode === "in-store" && hasShippingItems) || mode === "offsite";

    // Check if any section is being edited
    if (isEditingBoxes || (shouldCheckShipping && isEditingShipping)) {
      return true;
    }

    // Check if loading
    if (isLoading) {
      return true;
    }

    // Check if boxes are updated
    if (!isBoxesUpdated) {
      return true;
    }

    // Check if shipping is needed and updated
    if (shouldCheckShipping && !isShippingUpdated) {
      return true;
    }

    // Check if credit card is needed and updated
    const isCreditCardNeeded = !(isGiftCardApplied && giftCardAmount >= getTotalAmount());
    if (isCreditCardNeeded && !isCreditCardUpdated) {
      return true;
    }

    // Check if form is valid
    if (!isFormValid) {
      return true;
    }

    return false;
  };

  // Load the Stripe SDK once on mount
  useEffect(() => {
    let cancelled = false;
    stripePromise.then((stripe) => {
      if (cancelled || !stripe) return;
      stripeRef.current = stripe;
      // Disable Stripe Link entirely. Link is Stripe's saved-payment-method
      // service — it would let dealers (or anyone using these laptops at
      // events) "auto-fill" a previously-saved card from a different customer,
      // which could lead to wrong-card charges. We never want saved cards on
      // these dealer-event laptops.
      elementsRef.current = stripe.elements({
        // Skip loading external resources Stripe might use for payment-method
        // suggestions and saved-card UI.
        loader: "never",
      });
      setStripeReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Mount / unmount the Stripe card element when the card form becomes visible
  useEffect(() => {
    if (!stripeReady || !isEditingCreditCard) return;
    const elements = elementsRef.current;
    const mount = cardMountRef.current;
    if (!elements || !mount) return;

    let cardElement = cardElementRef.current;
    if (!cardElement) {
      cardElement = elements.create("card", {
        // Hide the postal code field. Stripe's AVS/ZIP check is optional and we don't
        // want dealers fumbling with it during in-person card swipes at events. Removing
        // it does NOT prevent the payment from succeeding — Stripe just skips the AVS
        // postal-code match. If we ever need it back, flip this to false.
        hidePostalCode: true,
        // Disable Stripe Link autocomplete on this element. Belt-and-suspenders
        // alongside loader: "never" on the Elements instance. We do NOT want
        // saved cards from other customers offered as suggestions on dealer
        // laptops — it's a real risk of charging the wrong card.
        disableLink: true,
        style: {
          base: {
            fontFamily: "PF DIN Text Pro, sans-serif",
            fontSize: "14px",
            color: "#424770",
            "::placeholder": { color: "#aab7c4" },
          },
          invalid: { color: "#9e2146" },
        },
      });
      cardElementRef.current = cardElement;
    }

    cardElement.mount(mount);
    const onChange = (event: { error?: { message?: string } }) => {
      setStripeError(event.error?.message);
      setIsCreditCardUpdated(false);
    };
    cardElement.on("change", onChange);

    return () => {
      try {
        cardElement!.off("change", onChange);
        cardElement!.unmount();
      } catch {
        // element may already be detached; ignore
      }
    };
  }, [stripeReady, isEditingCreditCard]);

  useEffect(() => {
    console.log("useEffect 1 triggered")
    const currentTotal = getTotalAmount();
    if (currentTotal === 0) {
      setIsCreditCardUpdated(true);
      setIsFormValid(true);
      setIsEditingCreditCard(false);
    }
    else if (currentTotal > 0 && !isCreditCardUpdated) {
      // Only reset credit card state if it was previously marked as updated
      // This prevents resetting when user is actively editing credit card
      setIsCreditCardUpdated(false);
      validateForm();
      setIsEditingCreditCard(true);
    }
  }, [discountValue, giftCardAmount, isBoxesUpdated]);


  // Add this useEffect to handle shipping section state
  useEffect(() => {
    console.log("useEffect 2 triggered")

    // For in-store orders without shipping items, automatically set shipping as updated and not editing
    if (mode === "in-store" && !hasShippingItems) {
      setIsEditingShipping(false);
      setIsShippingUpdated(true);
    }
  }, [mode, hasShippingItems]);
  // d useEffect to handle focus and Enter key press
  // Add useEffect for initial focus
  useEffect(() => {
    console.log("useEffect 3 triggered")

    if (isEditingBoxes && boxesInputRef.current) {
      boxesInputRef.current.focus();
    }
  }, [isEditingBoxes]);

  // When entering edit mode, default boxes to 1 if current value is 0 or empty
  useEffect(() => {
    if (isEditingBoxes) {
      try {
        if (!boxesCount || boxesCount.toString().trim() === "" || Number(boxesCount) === 0) {
          setBoxesCount("1");
        }
      } catch (e) {
        // ignore
      }
    }
  }, [isEditingBoxes]);

  useEffect(() => {
    console.log("useEffect 4 triggered")

    const handleKeyPress = (e: KeyboardEvent) => {
      // if (e.key === 'Enter' &&
      //   isFormValid &&
      //   isBoxesUpdated &&
      //   isShippingUpdated &&
      //   isCreditCardUpdated &&
      //   !isLoading &&
      //   !isEditingBoxes &&
      //   !isEditingShipping &&
      //   !isEditingCreditCard) {
      //   handlePlaceOrder();
      // }
    };

    if (
      isFormValid &&
      isBoxesUpdated &&
      isShippingUpdated &&
      isCreditCardUpdated &&
      !isLoading &&
      !isEditingBoxes &&
      !isEditingShipping &&
      !isEditingCreditCard
    ) {
      placeOrderButtonRef.current?.focus();
      document.addEventListener('keypress', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, [isFormValid, isBoxesUpdated, isShippingUpdated, isCreditCardUpdated, isLoading, isEditingBoxes, isEditingShipping, isEditingCreditCard]);
  
  // Update validation when form values or shipping fields change
  useEffect(() => {
    console.log("useEffect 5 triggered")
    validateForm();
  }, [boxesCount, company, contactName, address1, city, state, zip, phone, email, formik.values]);


  useEffect(() => {
    console.log("useEffect 6 triggered")

    console.log("Dealer: ", dealer)
    if (dealer?.percentageOff && !CartHelper.hasItemDiscount(filteredCart)) {
      handleDiscountUpdate(dealer?.percentageOff * 100)
    }
  }, [])

  // Add useEffect to handle post response
  useEffect(() => {
    console.log("useEffect 7 triggered")

    if (!isPostCompleted) {
      return;
    }

    if (error) {
      console.log("Error details:", error.response?.data);
      setErrorMessage(formatAxiosError(error));
      return;
    }

    console.log("response", response);
    if (response?.status === 201) {
      const _cart = response?.data;
      setOrderId?.(response?.data?.orderId);
      if (_cart?.errorOccured) {
        const errorDescription = parsePaymentSystemError(_cart?.errorDescription);
        const authResponseText = errorDescription && (errorDescription as { [key: string]: any })["auth_response_text"];

        // Handle payment error — re-open the card form for retry
        if (authResponseText?.toLowerCase().includes("invalid card number")) {
          setErrorPopupOpen(true);
          setIsEditingCreditCard(true);
          setIsCreditCardUpdated(false);
          setErrorMessage(errorDescription);
          return;
        }

        else {
          setErrorPopupOpen(true);
        }

        setErrorMessage(
          `${authResponseText
            ? authResponseText
            : "Unknown payment system error"
          }${errorDescription && !!(errorDescription as { [key: string]: any })["transaction_id"] ? ` (${(errorDescription as { [key: string]: any })["transaction_id"]})` : ""}`
        );
        return;
      }

      setAddressDialogOpen(false);
      navigate("/transaction-complete");
    }
  }, [isPostCompleted, error, response, navigate]);

  // Update isEditingBoxes when mode or hasShippingItems changes
  useEffect(() => {
    console.log("useEffect 8 triggered")

    if (isInStoreNoShipping) {
      setIsEditingBoxes(false);
      setIsBoxesUpdated(true);
      // Focus on credit card name field after a short delay to ensure the field is rendered
      setTimeout(() => {
        cardElementRef.current?.focus();
      }, 100);
    }
  }, [isInStoreNoShipping]);

 

  console.timeEnd("StartTime")
  return (
    <Box className="checkout-container">
      <Box className="checkout-main-wrapper">
        <PaymentFailurePopup message={errorMessage} open={errorPopupOpen} onClose={() => setErrorPopupOpen(false)} />
        {/* Left Section - Checkout */}
        <Box className="checkout-left-section">
          <Typography className="checkout-header" variant="h6" sx={{ fontFamily: "PF DIN Text Pro" }}>
            Checkout
          </Typography>

          {/* Number of Boxes */}
          <Box className="checkout-section">
            {isEditingBoxes ? (
              <>
                <Typography className="checkout-section-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  # of Boxes
                </Typography>
                <Box className="checkout-boxes-wrapper">
                  <CustomTextField
                    inputRef={boxesInputRef}
                    size="small"
                    sx={{ width: 120 }}
                    value={boxesCount}
                    onChange={handleBoxesChange}
                    onKeyPress={handleBoxesKeyPress}
                    type="number"
                    error={!!boxesError}
                    helperText={boxesError}
                    inputProps={{
                      min: 0,
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleBoxesUpdate}
                    sx={{
                      bgcolor: colors.primaryOzone500,
                      borderRadius: 2,
                      width: 270,
                      height: 40,
                      fontSize: "16px",
                      fontFamily: "PF DIN Text Pro",
                      fontWeight: 400,
                      textTransform: "none",
                      "&:disabled": {
                        bgcolor: colors.neutralGrey200,
                      },
                    }}
                    disabled={!boxesCount || boxesCount.trim() === ""}
                  >
                    Update Shipping & Handling
                  </Button>
                </Box>
              </>
            ) : (
              <Box className="checkout-boxes-display">
                <Typography className="checkout-section-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  {boxesCount} Boxes
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleBoxesEdit}
                  sx={{ padding: 0 }}
                >
                  <BorderColorOutlinedIcon sx={{ fontSize: 18, color: colors.neutralGrey800 }} />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Shipping Details */}
          {(hasShippingItems || hasOffsiteProducts(filteredCart)) && <Box className="checkout-shipping-wrapper">
            <Box className="checkout-shipping-header">
              <Typography className="checkout-shipping-title" variant="h6" sx={{ fontFamily: "PF DIN Text Pro" }}>
                Shipping Details
              </Typography>
              {!isEditingShipping && (
                <IconButton size="small" onClick={() => {
                  setIsEditingShipping(true);
                  setIsShippingUpdated(false);
                }} className="checkout-shipping-edit-button">
                  <BorderColorOutlinedIcon sx={{ fontSize: 18, color: colors.neutralGrey800 }} />
                </IconButton>
              )}
            </Box>
            {isEditingShipping ? (
              <Box className="checkout-shipping-form">
                {/* Address Type, Company & Contact Name in one line */}
                <Box className="checkout-shipping-field-group">
                  <Box className="checkout-shipping-field">
                    <Typography className="checkout-shipping-field-label" sx={{ fontFamily: "PF DIN Text Pro" }}>
                      Address Type
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3 }}>
                      <FormControlLabel
                        control={
                          <CustomCheckbox
                            checked={addressType === "residential"}
                            onChange={() => handleAddressTypeChange("residential")}
                            onKeyPress={handleAddressTypeKeyPress}
                          />
                        }
                        label={
                          <Typography sx={{ color: colors.neutralGrey800, fontFamily: "PF DIN Text Pro" }}>
                            Residential
                          </Typography>
                        }
                        sx={{ mr: 3 }}
                      />
                      <FormControlLabel
                        control={
                          <CustomCheckbox
                            checked={addressType === "commercial"}
                            onChange={() => handleAddressTypeChange("commercial")}
                            onKeyPress={handleAddressTypeKeyPress}
                          />
                        }
                        label={
                          <Typography sx={{ color: colors.neutralGrey800, fontFamily: "PF DIN Text Pro" }}>
                            Commercial
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>
                  <Box className="checkout-shipping-field">
                    <Typography className="checkout-shipping-field-label" sx={{ fontFamily: "PF DIN Text Pro" }}>
                      Company
                    </Typography>
                    <CustomTextField
                      inputRef={companyInputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Company"
                      required
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, contactNameInputRef)}
                    />
                  </Box>
                  <Box className="checkout-shipping-field">
                    <Typography className="checkout-shipping-field-label" sx={{ fontFamily: "PF DIN Text Pro" }}>
                      Contact name
                    </Typography>
                    <CustomTextField
                      inputRef={contactNameInputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Contact name"
                      required
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, address1InputRef)}
                    />
                  </Box>
                </Box>
                {/* Address Fields */}
                <Box
                  sx={{
                    mb: 1.5,
                    display: "flex",
                    gap: 2,
                    flexDirection: "row",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      Address 1
                    </Typography>
                    <CustomTextField
                      inputRef={address1InputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Street address or P.O. Box"
                      required
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, address2InputRef)}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      Address 2
                    </Typography>
                    <CustomTextField
                      inputRef={address2InputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Apt, suite, unit, building, floor, etc."
                      required
                      value={address2}
                      onChange={(e) => setAddress2(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, cityInputRef)}
                    />
                  </Box>
                </Box>
                {/* City, State, ZIP */}
                <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      City
                    </Typography>
                    <CustomTextField
                      inputRef={cityInputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      required
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, stateInputRef)}
                    />
                  </Box>
                  <Box sx={{ width: 200 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      State
                    </Typography>
                    <Select
                      inputRef={stateInputRef}
                      fullWidth
                      size="small"
                      value={state}
                      displayEmpty
                      required
                      onChange={(e) => setState(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, zipInputRef)}
                      sx={{
                        borderRadius: 1,
                        fontFamily: "PF DIN Text Pro",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.neutralGrey300,
                          transition: "border-color 0.1s",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.neutralGrey300,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.neutralGrey300,
                          boxShadow: `0 0 0 1px ${colors.neutralGrey500}`, // subtle blue shadow
                        },
                      }}
                    >
                      <MenuItem disabled value="">
                        Select
                      </MenuItem>
                      {states.map((item) => (
                        <MenuItem value={item.value}>{item.state}</MenuItem>
                      ))}

                    </Select>
                  </Box>
                  <Box sx={{ width: 120 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      ZIP
                    </Typography>
                    <CustomTextField
                      inputRef={zipInputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      required
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, phoneInputRef)}
                    />
                  </Box>
                </Box>
                {/* Contact Info */}
                <Box sx={{ display: "flex", gap: 2, mb: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      Phone no.
                    </Typography>
                    <CustomTextField
                      inputRef={phoneInputRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="(000) 000-0000"
                      required
                      value={phone ? phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3') : ''}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                        setPhone(value);
                      }}
                      onKeyPress={(e) => handleShippingFieldKeyPress(e, emailInputRef)}
                      inputProps={{
                        maxLength: 14, // (000) 000-0000 format
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        color: colors.neutralGrey800,
                        fontSize: "18px",
                        mb: 1,
                        fontFamily: "PF DIN Text Pro",
                      }}
                    >
                      Email Address
                    </Typography>
                    <CustomTextField
                      inputRef={emailInputRef}
                      fullWidth
                      type="email"
                      variant="outlined"
                      size="small"
                      required
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        formik.setFieldValue("email", e.target.value);
                        formik.setFieldTouched("email", true, false);
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && email && !formik.errors.email) {
                          handleShippingUpdate();
                        }
                      }}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Box>
                </Box>
                {/* Delivery Instructions */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    sx={{
                      color: colors.neutralGrey800,
                      fontSize: "18px",
                      mb: 1,
                      fontFamily: "PF DIN Text Pro",
                    }}
                  >
                    Delivery instructions (optional)
                  </Typography>
                  <CustomTextField fullWidth multiline rows={1} variant="outlined" size="small" value={deliveryInstructions} onChange={(e) => setDeliveryInstructions(e.target.value)} />
                </Box>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: colors.primaryOzone500,
                    borderRadius: 2,
                    width: 270,
                    fontSize: "16px",
                    fontFamily: "PF DIN Text Pro",
                    fontWeight: 400,
                    textTransform: "none",
                    "&:disabled": {
                      bgcolor: colors.neutralGrey200,
                    },
                  }}
                  startIcon={isAddressUpdateLoading ? <CircularProgress size={26} sx={{ color: colors.primaryOzone500 }} /> : null}
                  onClick={handleShippingUpdate}
                  disabled={!(company && contactName && address1 && city && state && zip && phone && email) || isAddressUpdateLoading}
                >
                  {isAddressUpdateLoading ? "Updating..." : "Update Shipping Details"}
                </Button>
              </Box>
            ) : (
              <Box className="checkout-shipping-display">
                <Typography className="checkout-shipping-display-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  Shipping Address
                </Typography>
                <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>{contactName}</Typography>
                <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  {address1}
                  {address2 ? `, ${address2}` : ""}
                </Typography>
                <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  {city}
                  {city && state ? ", " : ""}
                  {state} {zip}
                </Typography>
                <Box className="checkout-shipping-spacer" />
                <Typography className="checkout-shipping-display-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  Phone
                </Typography>
                <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>{phone}</Typography>
                <Box className="checkout-shipping-spacer" />
                <Typography className="checkout-shipping-display-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                  Email
                </Typography>
                <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>{email}</Typography>
                {deliveryInstructions && (
                  <>
                    <Box className="checkout-shipping-spacer" />
                    <Typography className="checkout-shipping-display-title" sx={{ fontFamily: "PF DIN Text Pro" }}>
                      Delivery instructions
                    </Typography>
                    <Typography className="checkout-shipping-display-text" sx={{ fontFamily: "PF DIN Text Pro" }}>{deliveryInstructions}</Typography>
                  </>
                )}
              </Box>
            )}
          </Box>}

          {/* Credit Card Info */}
          {!!(getTotalAmount()) && <Box>
            <Box className="checkout-shipping-header">
              <Typography className="checkout-shipping-title" variant="h6" sx={{ fontFamily: "PF DIN Text Pro" }}>
                Credit Card Info
              </Typography>
            </Box>
            <Box component="form" onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); handleCreditCardUpdate(); }} className="checkout-shipping-form checkout-credit-card-form">
                <Box className="checkout-shipping-field-group" sx={{ flexDirection: "column" }}>
                  <Box className="checkout-shipping-field">
                    <Typography className="checkout-shipping-field-label" sx={{ fontFamily: "PF DIN Text Pro" }}>
                      Name on card
                    </Typography>
                    <CustomTextField
                      inputRef={nameOnCardRef}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Name on card (optional)"
                      name="nameOnCard"
                      value={formik.values.nameOnCard}
                      onChange={(e) => {
                        formik.setFieldValue('nameOnCard', e.target.value);
                        setIsCreditCardUpdated(false);
                      }}
                      error={formik.touched.nameOnCard && Boolean(formik.errors.nameOnCard)}
                      helperText={formik.touched.nameOnCard && formik.errors.nameOnCard}
                    />
                  </Box>
                  <Box className="checkout-shipping-field" sx={{ mt: 2 }}>
                    <Typography className="checkout-shipping-field-label" sx={{ fontFamily: "PF DIN Text Pro", mb: 1 }}>
                      Card details
                    </Typography>
                    <Box
                      ref={cardMountRef}
                      sx={{
                        border: `1px solid ${colors.neutralGrey300}`,
                        borderRadius: 1,
                        padding: "10px 14px",
                        backgroundColor: colors.fieldBg || "#fff",
                        minHeight: "40px",
                      }}
                    />
                    {stripeError && (
                      <Typography sx={{ color: "#9e2146", fontSize: "12px", mt: 0.5, fontFamily: "PF DIN Text Pro" }}>
                        {stripeError}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Button
                  ref={updateCardRef}
                  variant="contained"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleCreditCardUpdate(); }}
                  sx={{
                    bgcolor: colors.primaryOzone500,
                    borderRadius: 2,
                    width: 270,
                    mt: 2,
                    fontSize: "16px",
                    fontFamily: "PF DIN Text Pro",
                    fontWeight: 400,
                    textTransform: "none",
                    "&:disabled": {
                      bgcolor: colors.neutralGrey200,
                    },
                  }}
                  disabled={!stripeReady || isStripeProcessing}
                >
                  {isStripeProcessing ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Update Credit Card"}
                </Button>
              </Box>
          </Box>}
        </Box>

        {/* Right Section - Purchase Summary */}
        <Box className="checkout-right-section">
          <Box className="checkout-summary-header">
            <Typography className="checkout-summary-title" variant="h6" sx={{ fontFamily: "PF DIN Text Pro" }}>
              Purchase Summary
            </Typography>
            <Typography className="checkout-summary-order-number" sx={{ fontFamily: "PF DIN Text Pro" }}>
              {`Order Number # ${filteredCart?.orderId ?? ""}`}
            </Typography>
          </Box>

          <Box className="checkout-summary-content">
            <Box className="checkout-summary-items">
              <Box className="checkout-summary-row">
                <Typography className="checkout-summary-label" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  Subtotal
                </Typography>
                <Typography className="checkout-summary-value" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  {formatCurrency(CartHelper.getCartSubtotal(filteredCart))}
                </Typography>
              </Box>
              <Box className="checkout-summary-row">
                <Typography className="checkout-summary-label" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  Eligible Co-Op applied
                </Typography>
                <Typography className="checkout-summary-value" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  -{formatCurrency(CartHelper.getSubtotalCoopTotal(filteredCart))}
                </Typography>
              </Box>
              <Box className="checkout-summary-row">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography className="checkout-summary-label" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                    Discounts
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ padding: 0 }}
                    disabled={CartHelper.hasItemDiscount(filteredCart)}
                    title={
                      CartHelper.hasItemDiscount(filteredCart)
                        ? "Remove item-level discounts to use an order-level discount"
                        : undefined
                    }
                    onClick={() => setDiscountDialogOpen(true)}
                  >
                    <BorderColorOutlinedIcon sx={{ fontSize: 18, color: colors.neutralGrey800 }} />
                  </IconButton>
                </Box>
                <Typography className="checkout-summary-value" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  -{formatCurrency((parseFloat(discountValue) || 0) + CartHelper.getCartItemDiscountTotal(filteredCart))}
                </Typography>
              </Box>
              {isGiftCardApplied && (
                <Box className="checkout-summary-row">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography className="checkout-summary-label" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                      Gift Card
                    </Typography>
                    <IconButton
                      size="small"
                      sx={{ padding: 0 }}
                      onClick={() => {
                        setGiftCardAmount(0);
                        // setGiftCardInput("");
                        setIsGiftCardApplied(false);
                      }}
                    >
                      <BorderColorOutlinedIcon sx={{ fontSize: 18, color: colors.neutralGrey800 }} />
                    </IconButton>
                  </Box>
                  <Typography className="checkout-summary-value" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                    -{formatCurrency(giftCardAmount)}
                  </Typography>
                </Box>
              )}
              <Box className="checkout-summary-row">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography className="checkout-summary-label" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                    Shipping & handling
                  </Typography>
                  <IconButton size="small" sx={{ padding: 0 }} onClick={() => setShippingDialogOpen(true)}>
                    <BorderColorOutlinedIcon sx={{ fontSize: 18, color: colors.neutralGrey800 }} />
                  </IconButton>
                </Box>
                <Typography className="checkout-summary-value" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  +{formatCurrency(getCurrentShippingFee())}
                </Typography>
              </Box>
              <Box className="checkout-summary-divider" />
              <Box className="checkout-summary-total">
                <Box className="checkout-summary-total-label">
                  <Typography className="checkout-summary-total-text" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "20px", fontWeight: 500 }}>
                    Total
                  </Typography>
                  <Typography className="checkout-summary-total-count" sx={{ fontFamily: "PF DIN Text Pro" }}>
                    ({filteredCart?.products?.length || 0} items)
                  </Typography>
                </Box>
                <Typography className="checkout-summary-total-amount" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "22px", fontWeight: 500 }}>

                  {formatCurrency((getTotalAmount() || 0))}
                </Typography>
              </Box>
              <Box className="checkout-summary-divider" />
              <Box className="checkout-gift-card">
                <Typography className="checkout-gift-card-title" sx={{ fontFamily: "PF DIN Text Pro", fontSize: "18px" }}>
                  Redeem Gift Card
                </Typography>
                <form onSubmit={e => {
                  e.preventDefault(); e.stopPropagation(); handleGiftCardUpdate();
                }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter gift card amount"
                    value={giftCardInput}
                    onChange={(e) => setGiftCardInput(e.target.value.replace(/[^\d.]/g, ""))}
                    disabled={isGiftCardApplied}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        padding: "12px",
                        paddingRight: "6px",
                        bgcolor: "#fff",
                        "& fieldset": {
                          borderColor: colors.neutralGrey300,
                        },
                        "&:hover fieldset": {
                          borderColor: colors.neutralGrey300,
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: colors.neutralGrey300,
                        },
                      },
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton

                            onClick={() => {
                              if (giftCardInput && !isGiftCardApplied) {
                                setGiftCardAmount(Number(giftCardInput));
                                setIsGiftCardApplied(true);
                              }
                            }}
                            disabled={!giftCardInput || isGiftCardApplied}
                            sx={{
                              color: colors.white,
                              background: colors.primaryOzone500,
                              padding: "16px 24px",
                              borderRadius: 2,
                              "&:disabled": {
                                background: colors.neutralGrey200,
                              },
                            }}
                          >
                            <ArrowForwardIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Box>
            </Box>

            <Box className="checkout-place-order-button">
              <Button
                ref={placeOrderButtonRef}
                variant="contained"
                fullWidth
                disabled={isDisabled()}
                onClick={handlePlaceOrder}
                sx={{
                  bgcolor: isFormValid && isBoxesUpdated && isShippingUpdated && isCreditCardUpdated && !isLoading ? colors.primaryOzone500 : colors.neutralGrey200,
                  color: isFormValid && isBoxesUpdated && isShippingUpdated && isCreditCardUpdated && !isLoading ? "#fff" : colors.neutralGrey600,
                  textTransform: "none",
                  py: 1.5,
                  fontFamily: "PF DIN Text Pro",
                  fontSize: "16px",
                  "&:hover": {
                    bgcolor: isFormValid && isBoxesUpdated && isShippingUpdated && isCreditCardUpdated && !isLoading ? colors.primaryOzone500 : colors.neutralGrey200,
                  },
                  "&.Mui-disabled": {
                    bgcolor: colors.neutralGrey200,
                    color: colors.neutralGrey600,
                  },
                }}
                startIcon={isLoading ? <CircularProgress size={28} sx={{ color: colors.primaryOzone500 }} /> : null}
              >
                {isLoading ? "Processing..." : "Place Order"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Dialogs */}
      <Dialog
        open={discountDialogOpen}
        onClose={() => setDiscountDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 0,
            background: '#fff',
            boxShadow: 24,
            minWidth: 400,
            width: 400,
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ p: 0, width: '100%' }}>
          {/* Title and Close */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            pt: 3,
            pb: 2,
          }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: 22,
              color: '#223354',
              fontFamily: 'PF DIN Text Pro',
              textAlign: 'center',
              flex: 1,
            }}>
              Order Level Discount
            </Typography>
            <IconButton
              onClick={() => setDiscountDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                background: colors.white,

              }}
            >
              <CancelIcon
                sx={{ color: colors.primaryOzone500, cursor: "pointer" }}
                style={{ width: 30, height: 30 }}
              />
            </IconButton>
          </Box>
          {/* Divider */}
          <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 0.5 }} />
          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            mb: 3,
            position: 'relative',
          }}>
            <Box
              sx={{
                fontWeight: discount.type === 'amount' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'amount' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'amount' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
                mr: 2,
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: "amount" }))}
            >
              Amount
            </Box>
            <Box
              sx={{
                fontWeight: discount.type === 'percentage' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'percentage' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'percentage' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: 'percentage' }))}
            >
              Percentage
            </Box>
            {/* Gray divider under tabs */}
            <Box sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '1px',
              bgcolor: '#E0E0E0',
              zIndex: 0,
            }} />
          </Box>
          <form onSubmit={e => {
            e.preventDefault(); e.stopPropagation(); handleDiscountUpdate();
          }}>
            {/* Input and Note */}
            <Box sx={{ px: 5, pb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <TextField
                fullWidth
                autoFocus
                value={discount.value}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d.]/g, "");
                  const parts = value.split('.');
                  if (parts.length > 2) return;
                  if (parts[1] && parts[1].length > 2) return;
                  if (value.startsWith('-')) return;
                  setDiscountWithType(prev => ({ ...prev, value: Number(value) }))
                }}
                InputProps={{
                  startAdornment: discount.type === 'amount' ? <span style={{ marginRight: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>$</span> : undefined,
                  endAdornment: discount.type === 'percentage' ? <span style={{ marginLeft: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>%</span> : undefined,
                  sx: {
                    alignSelf: 'center',
                    fontSize: 22,
                    width: '50%',
                    fontWeight: 500,
                    background: '#fff',
                    fontFamily: 'PF DIN Text Pro',
                    borderRadius: '12px',
                    height: 56,
                    // pl: 1,
                    '& input': {
                      textAlign: 'center',
                      fontSize: 22,
                      fontWeight: 500,
                      color: '#223354',
                      padding: '16px 0',
                    },
                  },
                }}
                sx={{
                  mb: 3,
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: 'none',
                }}
                inputProps={{
                  inputMode: 'decimal',
                }}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                value={discountNote}
                onChange={(e) => setDiscountNote(e.target.value)}
                placeholder="Enter a note"
                sx={{
                  mb: 3,
                  background: '#fff',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  border: '1px solid #E0E0E0',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    background: '#fff',
                    '& fieldset': {
                      borderColor: '#E0E0E0',
                    },
                  },
                  '& textarea': {
                    fontFamily: 'PF DIN Text Pro',
                    fontSize: 16,
                    color: '#A0A4AD',
                  },
                }}
              />
            </Box>
            {/* Button */}
            <Box sx={{ px: 0, pb: 0, pt: 0 }}>
              <Button
                type="submit"
                fullWidth
                sx={{
                  background: '#0074FF',
                  color: '#fff',
                  borderRadius: '0 0 16px 16px',
                  fontWeight: 500,
                  fontSize: 20,
                  fontFamily: 'PF DIN Text Pro',
                  py: 2,
                  boxShadow: 'none',
                  textTransform: 'none',
                  mt: 0,
                  '&:hover': { background: '#0059c1' },
                }}
              >
                Add Discount
              </Button>
            </Box>
          </form>
        </Box>
      </Dialog>

      <Dialog
        open={shippingDialogOpen}
        onClose={() => setShippingDialogOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0,
            background: '#fff',
            boxShadow: 24,
            minWidth: 420,
            width: 420,
          },
        }}
      >
        <Box sx={{ p: 0, pt: 3, pb: 0, px: 0, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', mb: 2, gap: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 20, color: '#223354', fontFamily: 'PF DIN Text Pro', textAlign: 'center', flex: 1 }}>
              Edit Shipping & Handling
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setShippingDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 10,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <CancelIcon style={{ width: 30, height: 30 }} sx={{ color: colors.primaryOzone500, width: 30, height: 30 }} />
            </IconButton>
          </Box>
          <Box sx={{ px: 4, pb: 0 }}>
            <Typography sx={{ fontWeight: 500, fontSize: 18, color: '#223354', mb: 1, fontFamily: 'PF DIN Text Pro', textAlign: 'left' }}>
              Enter Amount
            </Typography>
            <TextField
              fullWidth
              value={shippingAmount}
              onChange={(e) => setShippingAmount(e.target.value.replace(/[^\d.]/g, ""))}
              placeholder="$0.00"
              InputProps={{
                startAdornment: <span style={{ marginRight: 8, fontSize: 18, color: '#5A6473' }}>$</span>,
                sx: {
                  fontSize: 18,
                  fontWeight: 400,
                  background: '#fff',
                  fontFamily: 'PF DIN Text Pro',
                  borderRadius: 2,
                  height: 56,
                  pl: 1,
                  '& input': {
                    padding: '16px 14px',
                    fontSize: 18,
                    fontWeight: 500,
                  },
                },
              }}
              sx={{
                mb: 3,
                background: '#fff',
                borderRadius: 2,
                boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                border: '1px solid #E0E0E0',
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
            />
          </Box>
          <Box sx={{ px: 0, pt: 0 }}>
            <Button
              onClick={() => {
                setCustomShippingFee(Number(shippingAmount));
                setShippingDialogOpen(false);
              }}
              fullWidth
              sx={{
                bgcolor: '#0074FF',
                color: '#fff',
                borderRadius: '0 0 16px 16px',
                fontWeight: 500,
                fontSize: 20,
                py: 2,
                fontFamily: 'PF DIN Text Pro',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#005ce6' },
              }}
              className="checkout-dialog-button"
            >
              Apply Amount
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Address Verification Dialog */}
      <Dialog
        open={showAddressDialog}
        onClose={() => setShowAddressDialog(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
            p: 0,
            background: '#fff',
            boxShadow: 24,
          }
        }}
      >
        <Box sx={{ p: 4, pb: 3, minWidth: 400, maxWidth: 520, textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 600, fontSize: 26, mb: 1, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
            Verify your address
          </Typography>
          <Typography sx={{ color: '#5A6473', fontSize: 16, mb: 4, fontFamily: 'PF DIN Text Pro' }}>
            To ensure accurate delivery, consider the changed below
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', mb: 4 }}>
            <Box
              sx={{
                border: selectedAddress === 'suggested' ? '2px solid #1976d2' : '1.5px solid #E0E0E0',
                borderRadius: 2.5,
                px: 3,
                py: 2.5,
                minWidth: 200,
                background: selectedAddress === 'suggested' ? '#F7FAFF' : '#fff',
                cursor: 'pointer',
                boxShadow: selectedAddress === 'suggested' ? '0 0 0 2px #E3F0FF' : 'none',
                transition: 'all 0.2s',
              }}
              onClick={() => setSelectedAddress('suggested')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setSelectedAddress('suggested');
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Radio checked={selectedAddress === 'suggested'} sx={{ p: 0, mr: 1, color: '#1976d2' }} />
                <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                  Suggested Address
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 500, fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {suggestedAddress?.name}
              </Typography>
              <Typography sx={{ fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {suggestedAddress?.address1}
              </Typography>
              <Typography sx={{ fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {suggestedAddress?.cityStateZip}
              </Typography>
            </Box>
            <Box
              sx={{
                border: selectedAddress === 'original' ? '2px solid #1976d2' : '1.5px solid #E0E0E0',
                borderRadius: 2.5,
                px: 3,
                py: 2.5,
                minWidth: 200,
                background: selectedAddress === 'original' ? '#F7FAFF' : '#fff',
                cursor: 'pointer',
                boxShadow: selectedAddress === 'original' ? '0 0 0 2px #E3F0FF' : 'none',
                transition: 'all 0.2s',
              }}
              onClick={() => setSelectedAddress('original')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  setSelectedAddress('original');
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Radio checked={selectedAddress === 'original'} sx={{ p: 0, mr: 1, color: '#1976d2' }} />
                <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                  Original Address
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 500, fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {originalAddress.name}
              </Typography>
              <Typography sx={{ fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {originalAddress.address1}
              </Typography>
              <Typography sx={{ fontSize: 15, color: '#223354', fontFamily: 'PF DIN Text Pro' }}>
                {originalAddress.cityStateZip}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            fullWidth
            autoFocus={true}
            sx={{
              width: '60%',
              bgcolor: '#1976d2',
              color: '#fff',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: 20,
              py: 1.5,
              fontFamily: 'PF DIN Text Pro',
              textTransform: 'none',
              mt: 1,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1565c0' },
              '&:focus': {
                outline: 'none',
                boxShadow: 'none'
              }
            }}
            onClick={handleUseSelectedAddress}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleUseSelectedAddress();
              }
            }}
          >
            Use Selected Address
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default Checkout;
